import React, { useEffect, useId, useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { safeArray } from '../../utils/safe';
import type { Flavor } from '../../types/@types/flavor';
import type { Product } from '../../types/@types/product';
import type { LinePriceCalc, OrderItemInput } from '../../types/@types/order';
import { getFlavors } from '../../services/FlavorService';
import { priceCheck } from '../../services/OrderService';
import LinePriceBreakdown from '../Price/LinePriceBreakdown';
import { buildKitchenSummary } from '../../utils/kitchen';
import { Button } from '../ui';
import VoiceDictationButton from '../ui/VoiceDictationButton';

interface SelectedFlavor {
  id: string;
  name: string;
  qty: number;
}

interface Props {
  open: boolean;
  product: Product | null;
  quantity?: number;
  onClose: () => void;
  onConfirm: (payload: {
    flavors?: SelectedFlavor[];
    additions: Record<string, number>;
    exceptions: string[];
    linePriceCalc?: LinePriceCalc;
    kitchenSummary?: string;
  }) => void;
}

export default function FlavorSelectorModal({
  open,
  product,
  quantity = 1,
  onClose,
  onConfirm,
}: Props) {
  const titleId = useId();
  const { data: flavors } = useQuery({
    queryKey: ['flavors'],
    queryFn: getFlavors,
    enabled: open,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const [selected, setSelected] = useState<Record<string, number>>({});
  const [additions, setAdditions] = useState<Record<string, number>>({});
  const [exceptions, setExceptions] = useState<string[]>([]);
  const [calc, setCalc] = useState<LinePriceCalc | undefined>();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const flavorsCfg = product?.flavors?.enabled
    ? product.flavors
    : product?.maxFlavors
      ? { enabled: product.maxFlavors > 0, min: 0, max: product.maxFlavors }
      : null;
  const additionsCfg = product?.additions?.enabled
    ? product.additions?.options ?? []
    : [];
  const exceptionsCfg = product?.exceptions?.enabled
    ? product.exceptions?.options ?? []
    : [];

  useEffect(() => {
    if (!open) {
      setSelected({});
      setAdditions({});
      setExceptions([]);
      setCalc(undefined);
      setErrorMsg(null);
      setSearchQuery('');
    }
  }, [open]);

  const visibleFlavors = useMemo(() => {
    const all = safeArray(flavors).filter((flavor) => {
      if (
        flavorsCfg?.allowed &&
        flavorsCfg.allowed.length > 0 &&
        !flavorsCfg.allowed.includes(flavor.id)
      ) {
        return false;
      }
      return true;
    });
    if (!searchQuery.trim()) return all;
    const q = searchQuery.toLowerCase().trim();
    return all.filter((f) => f.name.toLowerCase().includes(q));
  }, [flavors, flavorsCfg, searchQuery]);

  if (!open || !product) return null;

  const totalSelected = Object.values(selected).reduce((s, v) => s + v, 0);
  const incrementFlavor = (id: string) => {
    if (flavorsCfg) {
      const max = flavorsCfg.max ?? Infinity;
      if (totalSelected >= max) return;
    }
    setSelected((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  };
  const decrementFlavor = (id: string) => {
    setSelected((prev) => {
      const value = (prev[id] || 0) - 1;
      if (value <= 0) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: value };
    });
  };

  const onIncAddition = (key: string, maxQty: number) =>
    setAdditions((prev) => {
      const next = { ...prev, [key]: Math.min(maxQty, (prev[key] ?? 0) + 1) };
      if (next[key] === 0) delete next[key];
      return next;
    });
  const onDecAddition = (key: string) =>
    setAdditions((prev) => {
      const nextQty = Math.max(0, (prev[key] ?? 0) - 1);
      const next = { ...prev, [key]: nextQty };
      if (nextQty === 0) delete next[key];
      return next;
    });

  const onToggleException = (key: string) =>
    setExceptions((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  useEffect(() => {
    if (!flavorsCfg) {
      setErrorMsg(null);
      return;
    }
    const min = flavorsCfg.min ?? 0;
    const max = flavorsCfg.max ?? Infinity;
    if (totalSelected < min)
      setErrorMsg(`Selecciona al menos ${min} sabor(es)`);
    else if (totalSelected > max)
      setErrorMsg(`Máximo ${max} sabor(es)`);
    else setErrorMsg(null);
  }, [totalSelected, flavorsCfg]);

  // Cálculo de precio síncrono e instantáneo en cliente (0ms de latencia, cero carga al servidor)
  useEffect(() => {
    if (!product) return;
    const base = Number(product.price) || 0;

    let additionsTotal = 0;
    if (additionsCfg && additionsCfg.length > 0) {
      for (const opt of additionsCfg) {
        const qty = additions[opt.key] ?? 0;
        if (qty > 0) {
          additionsTotal += (Number(opt.priceDelta) || 0) * qty;
        }
      }
    }

    let exceptionsTotal = 0;
    if (exceptionsCfg && exceptionsCfg.length > 0) {
      for (const opt of exceptionsCfg) {
        if (exceptions.includes(opt.key) && (opt as any).affectsPrice) {
          exceptionsTotal += (Number(opt.priceDelta) || 0);
        }
      }
    }

    const flavorsTotal = 0;
    const unitPrice = Math.max(0, base + flavorsTotal + additionsTotal - exceptionsTotal);
    const final = unitPrice * quantity;

    setCalc({
      base,
      flavors: flavorsTotal,
      additions: additionsTotal,
      exceptions: exceptionsTotal,
      final,
    });
  }, [product, quantity, additions, exceptions, additionsCfg, exceptionsCfg]);

  const handleConfirm = () => {
    if (errorMsg) return;
    const selectedFlavors: SelectedFlavor[] = Object.entries(selected).map(
      ([id, qty]) => {
        const flavor = flavors?.find((f) => String(f.id) === String(id));
        return { id: String(id), name: flavor?.name || (id ? `Sabor #${id}` : ''), qty };
      },
    );
    const flavorsNames = selectedFlavors.map((f) => f.name);
    const kitchenSummary = buildKitchenSummary({
      flavorsNames,
      additions,
      additionsCatalog: additionsCfg,
      exceptions,
      exceptionsCatalog: exceptionsCfg,
    });
    onConfirm({
      flavors: selectedFlavors,
      additions,
      exceptions,
      linePriceCalc: calc,
      kitchenSummary,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-6">
      <div
        className="absolute inset-0 bg-black/35 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-[1] flex max-h-[90vh] w-full max-w-md sm:max-w-xl md:max-w-2xl lg:max-w-3xl flex-col overflow-hidden rounded-2xl border border-divider bg-surface shadow-[0_12px_32px_rgba(15,23,42,0.28)] transition-all duration-200"
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex flex-col gap-1">
            <h2 id={titleId} className="text-lg font-bold text-text text-[color:var(--color-text)]">
              Seleccionar Sabores
            </h2>
            {flavorsCfg && (
              <p className="text-sm text-text-muted">
                Seleccionados{' '}
                <span className="font-bold text-primary">{totalSelected}</span>/
                {flavorsCfg.max ?? '∞'}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-text-muted hover:bg-muted hover:text-text transition"
            aria-label="Cerrar modal"
          >
            <X size={18} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {safeArray(flavors).length > 6 && (
            <div className="relative mb-3.5 flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar sabor..."
                  className="w-full rounded-xl border border-border bg-surface pl-9 pr-8 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full bg-muted text-xs text-text-muted hover:text-text"
                    aria-label="Limpiar búsqueda"
                  >
                    ✕
                  </button>
                )}
              </div>
              <VoiceDictationButton
                size="sm"
                className="flex-shrink-0"
                onTranscript={(text) => setSearchQuery(text)}
              />
            </div>
          )}

          {flavorsCfg && (
            visibleFlavors.length > 0 ? (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {visibleFlavors.map((flavor) => {
                  const qty = selected[flavor.id] || 0;
                  return (
                    <li
                      key={flavor.id}
                      className={`flex items-center justify-between rounded-xl border px-3.5 py-2.5 transition-all select-none ${
                        qty > 0
                          ? 'border-emerald-500/60 bg-emerald-50/50 dark:bg-emerald-950/20 shadow-xs ring-1 ring-emerald-500/20'
                          : 'border-border bg-surface hover:border-border/80'
                      }`}
                    >
                      <span className="text-sm font-medium text-text truncate pr-2" title={flavor.name}>
                        {flavor.name}
                      </span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-base font-bold text-text transition hover:bg-muted active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:opacity-30 disabled:pointer-events-none"
                          onClick={() => decrementFlavor(flavor.id)}
                          disabled={qty === 0}
                          aria-label={`Quitar ${flavor.name}`}
                        >
                          -
                        </button>
                        <span className="min-w-[2.5ch] text-center text-sm sm:text-base font-bold tabular-nums text-text">
                          {qty}
                        </span>
                        <button
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-base font-bold text-text transition hover:bg-muted active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:opacity-30 disabled:pointer-events-none"
                          onClick={() => incrementFlavor(flavor.id)}
                          disabled={
                            flavorsCfg &&
                            totalSelected >= (flavorsCfg.max ?? Infinity)
                          }
                          aria-label={`Agregar ${flavor.name}`}
                        >
                          +
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="py-6 text-center text-sm text-text-muted">
                No se encontraron sabores con "{searchQuery}".
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="ml-2 font-semibold text-primary hover:underline"
                >
                  Ver todos
                </button>
              </div>
            )
          )}

          {additionsCfg.length > 0 && (
            <section className="mt-4">
              <h4 className="mb-2 text-sm font-semibold text-text">Adiciones</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {additionsCfg
                  .filter((o) => o.active)
                  .map((opt) => {
                    const qty = additions[opt.key] ?? 0;
                    return (
                      <div
                        key={opt.key}
                        className={`flex items-center justify-between rounded-xl border px-3.5 py-2.5 transition-all select-none ${
                          qty > 0
                            ? 'border-primary/50 bg-primary/5 shadow-xs ring-1 ring-primary/20'
                            : 'border-border bg-surface hover:border-border/80'
                        }`}
                      >
                        <span className="text-sm font-medium text-text truncate pr-2">
                          {opt.name}{' '}
                          <span className="text-xs text-text-muted">(+${opt.priceDelta})</span>
                        </span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-base font-bold text-text transition hover:bg-muted active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:opacity-30 disabled:pointer-events-none"
                            onClick={() => onDecAddition(opt.key)}
                            disabled={qty <= 0}
                            aria-label={`Quitar ${opt.name}`}
                          >
                            -
                          </button>
                          <span className="min-w-[2.5ch] text-center text-sm sm:text-base font-bold tabular-nums text-text">
                            {qty}
                          </span>
                          <button
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-base font-bold text-text transition hover:bg-muted active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:opacity-30 disabled:pointer-events-none"
                            onClick={() => onIncAddition(opt.key, opt.maxQty)}
                            disabled={qty >= opt.maxQty}
                            aria-label={`Agregar ${opt.name}`}
                          >
                            +
                          </button>
                          <span className="text-xs text-text-muted">
                            / {opt.maxQty}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </section>
          )}

          {exceptionsCfg.length > 0 && (
            <section className="mt-4">
              <h4 className="mb-2 text-sm font-semibold text-text">Sin / Excepciones</h4>
              <div className="flex flex-wrap gap-2">
                {exceptionsCfg
                  .filter((o) => o.active)
                  .map((opt) => {
                    const on = exceptions.includes(opt.key);
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => onToggleException(opt.key)}
                        className={`inline-flex min-h-[2.5rem] items-center justify-center rounded-full border px-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 ${on
                            ? 'border-primary bg-primary text-white'
                            : 'border-border bg-surface text-text hover:bg-muted'
                          }`}
                        aria-pressed={on}
                      >
                        {opt.name}
                      </button>
                    );
                  })}
              </div>
            </section>
          )}

          <div className="mt-4">
            <LinePriceBreakdown calc={calc} />
          </div>
        </div>
        <footer className="sticky bottom-0 border-t border-border bg-white px-5 py-3 shadow-receipt">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs font-semibold text-red-500" role="status">
              {errorMsg ?? ''}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="md" onClick={onClose}>
                Cancelar
              </Button>
              <Button variant="primary" size="md" disabled={!!errorMsg} onClick={handleConfirm}>
                Confirmar
              </Button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
